import { useEffect, useRef, useState } from 'react'
import { Settings, X, Check, Mic, Volume2, RotateCcw } from 'lucide-react'
import { Track } from 'livekit-client'
import { useVoiceStore } from '../../stores/voiceStore'
import { useLivekitStore } from '../../stores/livekitStore'
import { useAuthStore } from '../../stores/authStore'
import { useToastStore } from '../../stores/toastStore'
import { authApi } from '../../services/api'
import { requestVideoStream } from '../../services/cameraPermission'
import { dialog } from '../../services/dialog'

// OSPROD-103 (Discord-style): a gear in the voice controls opens a
// "Voice & video settings" modal with three test/setup sections:
//   • Camera   — live preview, Mirror toggle, device list.
//   • Microphone — device list + a LIVE input level meter so the user can
//     confirm the mic is picking up sound.
//   • Speaker  — device list + a "Test" tone routed to the chosen output.
//
// Safety: device selection reuses the OSPROD-100/101 voiceStore state so
// the modal and the chevron quick-pickers stay in sync. The camera preview
// is a one-shot, video-only getUserMedia torn down on close. The mic meter
// TAPS THE EXISTING published mic track (read-only AnalyserNode) — it never
// opens a second audio capture, so the denoiser cascade / voice-critical
// invariant (no getUserMedia({audio:true}) outside applyDenoiserMic) is
// untouched. Audio device labels come from the in-call permission already
// granted, so no audio getUserMedia is needed for enumeration either.

interface DeviceInfo {
  deviceId: string
  label: string
}

export default function VoiceVideoSettingsButton() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-10 h-10 rounded-full bg-muted text-foreground hover:bg-muted/80 flex items-center justify-center transition"
        title="Voice & video settings"
      >
        <Settings size={20} />
      </button>
      {open && <SettingsModal onClose={() => setOpen(false)} />}
    </>
  )
}

function SettingsModal({ onClose }: { onClose: () => void }) {
  const videoInputDeviceId = useVoiceStore((s) => s.videoInputDeviceId)
  const setVideoInputDevice = useVoiceStore((s) => s.setVideoInputDevice)
  const audioInputDeviceId = useVoiceStore((s) => s.audioInputDeviceId)
  const setAudioInputDevice = useVoiceStore((s) => s.setAudioInputDevice)
  const audioOutputDeviceId = useVoiceStore((s) => s.audioOutputDeviceId)
  const setAudioOutputDevice = useVoiceStore((s) => s.setAudioOutputDevice)
  const selfMirror = useVoiceStore((s) => s.selfMirror)
  const setSelfMirror = useVoiceStore((s) => s.setSelfMirror)

  const [cameras, setCameras] = useState<DeviceInfo[]>([])
  const [mics, setMics] = useState<DeviceInfo[]>([])
  const [speakers, setSpeakers] = useState<DeviceInfo[]>([])
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [micLevel, setMicLevel] = useState(0)
  const [micHint, setMicHint] = useState<string | null>(null)
  const [resettingVolumes, setResettingVolumes] = useState(false)

  const videoRef = useRef<HTMLVideoElement>(null)
  const previewStreamRef = useRef<MediaStream | null>(null)
  const sinkSupported = 'setSinkId' in HTMLMediaElement.prototype

  // ── Enumerate devices ──────────────────────────────────────────────
  // In a call, camera+mic permissions are already granted, so labels are
  // available. We still do a one-shot VIDEO grant to be safe for the
  // camera labels (video-only — not the audio invariant); audio labels
  // come from the existing in-call mic grant (no audio capture here).
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        let perm: MediaStream | null = null
        try {
          // OSPROD-145: cameraPermission circuit breaker.
          perm = await requestVideoStream({ video: true })
        } catch {
          /* camera blocked → labels may be anonymous */
        }
        const all = await navigator.mediaDevices.enumerateDevices()
        if (perm) perm.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } })
        if (cancelled) return

        setCameras(
          all.filter((d) => d.kind === 'videoinput')
            .map((d) => ({ deviceId: d.deviceId, label: d.label || `Camera ${d.deviceId.slice(0, 4)}` })),
        )
        setMics(
          all.filter((d) => d.kind === 'audioinput' && d.deviceId !== 'communications')
            .map((d) => ({ deviceId: d.deviceId, label: d.label || `Microphone ${d.deviceId.slice(0, 4)}` })),
        )
        const outs = all.filter((d) => d.kind === 'audiooutput' && d.deviceId !== 'communications')
          .map((d) => ({ deviceId: d.deviceId, label: d.label || `Speaker ${d.deviceId.slice(0, 4)}` }))
        setSpeakers(outs)

        if (!useVoiceStore.getState().videoInputDeviceId && all.some((d) => d.kind === 'videoinput')) {
          const cam = all.find((d) => d.kind === 'videoinput')
          if (cam) setVideoInputDevice(cam.deviceId)
        }
      } catch {
        /* enumerate unavailable */
      }
    })()
    return () => { cancelled = true }
  }, [setVideoInputDevice])

  // ── Camera preview (one-shot, video-only, torn down on close) ──────
  useEffect(() => {
    let cancelled = false
    const stop = () => {
      if (previewStreamRef.current) {
        previewStreamRef.current.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } })
        previewStreamRef.current = null
      }
      if (videoRef.current) videoRef.current.srcObject = null
    }
    const start = async () => {
      stop()
      try {
        // OSPROD-145: cameraPermission circuit breaker.
        const stream = await requestVideoStream({
          video: videoInputDeviceId ? { deviceId: { exact: videoInputDeviceId } } : true,
        })
        if (cancelled) { stream.getTracks().forEach((t) => { try { t.stop() } catch { /* noop */ } }); return }
        previewStreamRef.current = stream
        setPreviewError(null)
        if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play().catch(() => {}) }
      } catch {
        if (!cancelled) setPreviewError('Preview unavailable — camera busy or blocked.')
      }
    }
    start()
    return () => { cancelled = true; stop() }
  }, [videoInputDeviceId])

  // ── Mic level meter: tap the EXISTING published mic track ──────────
  useEffect(() => {
    let raf = 0
    let ctx: AudioContext | null = null
    let src: MediaStreamAudioSourceNode | null = null
    let cancelled = false

    const start = () => {
      const room = useLivekitStore.getState().room
      if (!room) { setMicHint('Join a call to test your microphone.'); return }
      const micPub = Array.from(room.localParticipant.trackPublications.values())
        .find((p) => p.source === Track.Source.Microphone)
      const mst = micPub?.track?.mediaStreamTrack
      if (!mst || mst.readyState !== 'live' || micPub?.isMuted) {
        setMicHint('Unmute your microphone to see the input level.')
        setMicLevel(0)
        return
      }
      setMicHint(null)
      try {
        ctx = new AudioContext()
        src = ctx.createMediaStreamSource(new MediaStream([mst]))
        const analyser = ctx.createAnalyser()
        analyser.fftSize = 512
        src.connect(analyser)
        const data = new Uint8Array(analyser.frequencyBinCount)
        const loop = () => {
          if (cancelled) return
          analyser.getByteTimeDomainData(data)
          let peak = 0
          for (let i = 0; i < data.length; i++) {
            const v = Math.abs(data[i] - 128)
            if (v > peak) peak = v
          }
          setMicLevel(Math.min(1, peak / 110))
          raf = requestAnimationFrame(loop)
        }
        loop()
      } catch {
        setMicHint('Mic level unavailable in this browser.')
      }
    }
    // Small delay lets a just-switched device finish republishing.
    const t = setTimeout(start, 250)

    return () => {
      cancelled = true
      clearTimeout(t)
      if (raf) cancelAnimationFrame(raf)
      try { src?.disconnect() } catch { /* noop */ }
      try { ctx?.close() } catch { /* noop */ }
    }
  }, [audioInputDeviceId])

  const handleCamera = async (deviceId: string) => {
    setVideoInputDevice(deviceId)
    if (useVoiceStore.getState().currentChannelId) {
      try { await useLivekitStore.getState().setVideoDevice(deviceId) } catch { /* noop */ }
    }
  }

  const handleMic = async (deviceId: string) => {
    setAudioInputDevice(deviceId)
    if (useVoiceStore.getState().currentChannelId) {
      try { await useLivekitStore.getState().setAudioDevice(deviceId) } catch { /* noop */ }
    }
  }

  // OSPROD-144: the per-peer volume slider was removed in 1.1.21A but the
  // backing data (User.settings.peerCustomizations, keyed by userId) is still
  // read by LiveKitBackgroundAudio's gain stage on every call. Anyone who
  // touched the slider before its removal has those values silently applied
  // forever — manifests as "I hear X way quieter / louder than everyone else
  // does". This button wipes the stored entries; the next-load effect in
  // VoiceManager picks up the empty object and clears voiceStore.peerVolumes.
  const handleResetPeerVolumes = async () => {
    if (resettingVolumes) return
    const user = useAuthStore.getState().user
    if (!user) return
    const settings = (user.settings as Record<string, unknown>) ?? {}
    const existing = settings.peerCustomizations as Record<string, unknown> | undefined
    if (!existing || Object.keys(existing).length === 0) {
      useToastStore.getState().showToast({
        id: 'reset-peer-volumes-noop',
        type: 'message',
        title: 'Nothing to reset',
        body: 'Per-peer volumes are already at the defaults.',
      })
      return
    }
    if (!(await dialog.confirm({ message: 'Reset per-peer volume overrides for everyone you talk to?', tone: 'warning', confirmLabel: 'Reset' }))) return
    setResettingVolumes(true)
    try {
      const res = await authApi.updateMe({
        settings: { ...settings, peerCustomizations: {} },
      })
      useAuthStore.setState({ user: res.data })
      // Belt-and-braces: the VoiceManager load effect re-fires from the
      // settings change above and clears the store, but call it directly so
      // the gain stage flips to 1.0 even if some other listener happens to
      // mutate voiceStore in the same tick.
      useVoiceStore.getState().loadPeerCustomizations({})
      useToastStore.getState().showToast({
        id: 'reset-peer-volumes',
        type: 'message',
        title: 'Per-peer volumes reset',
        body: 'Everyone in your calls now plays at the standard volume.',
      })
    } catch {
      useToastStore.getState().showToast({
        id: 'reset-peer-volumes-fail',
        type: 'error',
        title: 'Reset failed',
        body: 'Try again in a few seconds.',
      })
    } finally {
      setResettingVolumes(false)
    }
  }

  const testSpeaker = async () => {
    try {
      const ctx = new AudioContext()
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      gain.gain.value = 0.08
      osc.frequency.value = 440
      const dest = ctx.createMediaStreamDestination()
      osc.connect(gain).connect(dest)
      const el = new Audio()
      el.srcObject = dest.stream
      if (sinkSupported && audioOutputDeviceId && audioOutputDeviceId !== 'default') {
        try { await (el as HTMLAudioElement & { setSinkId(id: string): Promise<void> }).setSinkId(audioOutputDeviceId) } catch { /* fall back to default */ }
      }
      await el.play().catch(() => {})
      osc.start()
      setTimeout(() => {
        try { osc.stop() } catch { /* noop */ }
        try { el.pause() } catch { /* noop */ }
        try { ctx.close() } catch { /* noop */ }
      }, 500)
    } catch {
      /* AudioContext blocked */
    }
  }

  const deviceList = (
    items: DeviceInfo[],
    selected: string | null,
    onPick: (id: string) => void,
    emptyLabel: string,
  ) => (
    <div className="max-h-32 overflow-y-auto">
      {items.length === 0 && <div className="text-xs text-muted-foreground py-1">{emptyLabel}</div>}
      {items.map((d) => (
        <button
          key={d.deviceId}
          onClick={() => onPick(d.deviceId)}
          className="w-full text-left px-2 py-1.5 text-xs rounded text-muted-foreground hover:bg-primary/90 hover:text-white transition flex items-center gap-2"
        >
          {selected === d.deviceId && <Check size={12} className="text-green-500 shrink-0" />}
          <span className="flex-1 truncate">{d.label}</span>
        </button>
      ))}
    </div>
  )

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70" />
      <div
        className="relative z-10 bg-card rounded-lg w-[480px] max-w-[92vw] max-h-[88vh] overflow-y-auto p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Voice &amp; video settings</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" title="Close">
            <X size={20} />
          </button>
        </div>

        {/* ── Camera ── */}
        <div className="text-[10px] font-semibold text-muted-foreground uppercase mb-1">Camera</div>
        <div className="relative w-full aspect-video bg-muted rounded-lg overflow-hidden mb-2 ring-1 ring-border">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover ${selfMirror ? '-scale-x-100' : ''}`}
          />
          {previewError && (
            <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground px-4 text-center">
              {previewError}
            </div>
          )}
        </div>
        <label className="flex items-center justify-between py-1.5">
          <span className="text-sm text-foreground">Mirror my video</span>
          <button
            onClick={() => setSelfMirror(!selfMirror)}
            className={`relative w-10 h-6 rounded-full transition ${selfMirror ? 'bg-primary' : 'bg-muted-foreground/30'}`}
            role="switch"
            aria-checked={selfMirror}
            title="Mirror my self-view"
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${selfMirror ? 'translate-x-4' : ''}`} />
          </button>
        </label>
        {deviceList(cameras, videoInputDeviceId, handleCamera, 'No cameras available')}

        {/* ── Microphone ── */}
        <div className="text-[10px] font-semibold text-muted-foreground uppercase mt-4 mb-1 flex items-center gap-1">
          <Mic size={10} /> Microphone
        </div>
        <div className="mb-2">
          <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden ring-1 ring-border">
            <div
              className="h-full bg-green-500 transition-[width] duration-75"
              style={{ width: `${Math.round(micLevel * 100)}%` }}
            />
          </div>
          {micHint && <div className="text-[11px] text-muted-foreground mt-1">{micHint}</div>}
        </div>
        {deviceList(mics, audioInputDeviceId, handleMic, 'No microphones available')}

        {/* ── Speaker ── */}
        <div className="text-[10px] font-semibold text-muted-foreground uppercase mt-4 mb-1 flex items-center gap-1">
          <Volume2 size={10} /> Speaker
        </div>
        {!sinkSupported && (
          <div className="text-[11px] text-muted-foreground mb-1">
            This browser always uses the system default output device.
          </div>
        )}
        <button
          onClick={testSpeaker}
          className="mb-2 px-3 py-1.5 text-xs rounded bg-muted text-foreground hover:bg-muted/80 transition"
        >
          Test sound
        </button>
        {sinkSupported && deviceList(speakers, audioOutputDeviceId, setAudioOutputDevice, 'No speakers available')}

        {/* ── Other ── OSPROD-144 escape hatch for legacy peer-volume data ── */}
        <div className="text-[10px] font-semibold text-muted-foreground uppercase mt-4 mb-1">Other</div>
        <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
          <div className="text-sm text-foreground font-medium">Reset per-peer volumes</div>
          <div className="text-[11px] text-muted-foreground leading-snug">
            If specific people sound much quieter or louder than the rest of the call — and others hear them fine — you may have per-person volume overrides saved from an older version of the app. The slider that set them was removed, but the saved values still affect playback. Reset to make everyone play at the standard volume.
          </div>
          <button
            onClick={handleResetPeerVolumes}
            disabled={resettingVolumes}
            className="px-3 py-1.5 text-xs rounded bg-destructive/15 text-destructive hover:bg-destructive/25 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center gap-1.5"
          >
            <RotateCcw size={12} /> {resettingVolumes ? 'Resetting…' : 'Reset per-peer volumes'}
          </button>
        </div>
      </div>
    </div>
  )
}
